import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import {
  Box,
  Stack,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Checkbox,
  ListItemText,
  Menu,
  Divider,
  ButtonBase,
  IconButton,
  ListSubheader,
  InputAdornment,
  Button
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from '@mui/icons-material/Search';
import { useLanguage } from '../../../utils/i18n';
import { useVtdsList } from '../../../stores/vtdsStore';
import { useCompanyList } from '../../../stores/companyStore';
import { useDriverAssignList } from '../../../stores/driverAssignStore';
import { apiHowen, apiVss } from '../../../api/axiosClient';
import DataTable from '../../../components/DataTable';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs from 'dayjs';

// Import komponen DetailReportFatiguePage yang sudah dirombak menjadi Dialog
import DetailReportFatiguePage from './DetailReportFatigue';

const normalizePlateStr = (s) =>
  String(s || '')
    .replace(/\s+/g, '')
    .toUpperCase();

const findDriverNameForAlarm = (alarmItem, assignments) => {
  if (!alarmItem || !Array.isArray(assignments) || assignments.length === 0) {
    return alarmItem?.drivername || '-';
  }

  const targetPlate = normalizePlateStr(alarmItem?.plateNo ?? alarmItem?.vehicle);
  const targetImei = normalizePlateStr(alarmItem?.deviceno ?? alarmItem?.deviceguid);
  if (!targetPlate && !targetImei) {
    return alarmItem?.drivername || '-';
  }

  const rawTime = alarmItem?.reportTime || alarmItem?.createtime || alarmItem?.createTime;
  const alarmDay = rawTime ? dayjs(rawTime) : null;
  const alarmDateStr = alarmDay && alarmDay.isValid() ? alarmDay.format('YYYY-MM-DD') : null;

  const candidates = assignments.filter((assign) => {
    const plates = [assign?.license_plate, assign?.vehicle_name, assign?.join?.vehicle_license_plate, assign?.join?.vehicle_name]
      .map(normalizePlateStr)
      .filter(Boolean);

    const assignImei = normalizePlateStr(assign?.imei_no);
    const isVehicleMatch = (targetPlate && plates.includes(targetPlate)) || (targetImei && assignImei && assignImei === targetImei);

    if (!isVehicleMatch) return false;

    if (alarmDateStr) {
      const validAtDay = assign?.valid_at ? dayjs(assign.valid_at) : null;
      const validAtStr = validAtDay && validAtDay.isValid() ? validAtDay.format('YYYY-MM-DD') : null;

      const expAtDay = assign?.exp_at ? dayjs(assign.exp_at) : null;
      const expAtStr = expAtDay && expAtDay.isValid() ? expAtDay.format('YYYY-MM-DD') : null;

      if (validAtStr && alarmDateStr < validAtStr) return false;
      if (expAtStr && alarmDateStr > expAtStr) return false;
    }

    return true;
  });

  if (candidates.length === 0) {
    return alarmItem?.drivername || '-';
  }

  candidates.sort((a, b) => {
    const va = a?.valid_at ? dayjs(a.valid_at).format('YYYY-MM-DD') : '';
    const vb = b?.valid_at ? dayjs(b.valid_at).format('YYYY-MM-DD') : '';
    if (va !== vb) return vb.localeCompare(va);
    return Number(b?.id || 0) - Number(a?.id || 0);
  });

  const matched = candidates[0];
  return matched?.driver_name || matched?.join?.driver_name || alarmItem?.drivername || '-';
};

export default function ReportFatiguePage() {
  return (
    <>
      <ReportFatiguePageContent />
    </>
  );
}

function ReportFatiguePageContent() {
  const { t, language } = useLanguage();
  const [rangeOption, setRangeOption] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const [selectedCompanies, setSelectedCompanies] = useState(() => {
    const storedLocal = localStorage.getItem('companyId');
    return storedLocal && storedLocal !== 'all' ? [String(storedLocal)] : [];
  });

  const [assignedVehicles, setAssignedVehicles] = useState([]);
  const [selectedAlrms, setSelectedAlrms] = useState([]);

  const [searchQuery, setSearchQuery] = useState('');
  const [searchField, setSearchField] = useState('vehicle');
  const fatigueSearchOptions = [
    { label: 'Vehicle', value: 'vehicle' },
    { label: 'Driver', value: 'drivername' },
    { label: 'Alarm Type', value: 'alarmTypeValue' }
  ];

  const { companyOptions: rawCompanyOptions } = useCompanyList();

  const companyOptions = useMemo(() => {
    const map = new Map();
    (rawCompanyOptions || []).forEach((c) => {
      const val = String(c.value ?? c.id ?? c);
      if (!map.has(val) && val !== '') {
        map.set(val, {
          value: val,
          label: c.label ?? c.name ?? String(c.value ?? c.id ?? c)
        });
      }
    });
    return Array.from(map.values());
  }, [rawCompanyOptions]);

  const [companySearchText, setCompanySearchText] = useState('');

  const filteredCompanyOptions = useMemo(() => {
    if (!companySearchText) return companyOptions;
    return companyOptions.filter((c) => c.label.toLowerCase().includes(companySearchText.toLowerCase()));
  }, [companyOptions, companySearchText]);

  const isAllCompaniesSelected = companyOptions.length > 0 && selectedCompanies.length === companyOptions.length;
  const isIndeterminateCompanies = selectedCompanies.length > 0 && selectedCompanies.length < companyOptions.length;

  const renderCompanyValue = (selected) => {
    if (!selected || selected.length === 0) return 'All Companies';
    if (selected.length === companyOptions.length) return 'All Companies Selected';
    return `${selected.length} Company Selected`;
  };

  const handleCompanyChange = (event) => {
    const { value } = event.target;
    const selectedValues = typeof value === 'string' ? value.split(',') : value;
    if (selectedValues.includes('select-all-option')) {
      if (isAllCompaniesSelected) {
        setSelectedCompanies([]);
      } else {
        setSelectedCompanies(companyOptions.map((c) => c.value));
      }
    } else {
      setSelectedCompanies(selectedValues);
    }
  };

  const filterCompanyIds = useMemo(() => {
    if (!selectedCompanies || selectedCompanies.length === 0) return '';
    const hasAll = selectedCompanies.some((s) => String(s) === 'all');
    if (hasAll) return '';
    return selectedCompanies.join(',');
  }, [selectedCompanies]);

  const prevCompanyIdsRef = useRef(filterCompanyIds);
  useEffect(() => {
    if (prevCompanyIdsRef.current !== filterCompanyIds) {
      setAssignedVehicles([]);
      prevCompanyIdsRef.current = filterCompanyIds;
    }
  }, [filterCompanyIds]);

  const [vehicleSearchText, setVehicleSearchText] = useState('');
  const [debouncedVehicleSearch, setDebouncedVehicleSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedVehicleSearch(vehicleSearchText);
    }, 600);
    return () => clearTimeout(timer);
  }, [vehicleSearchText]);

  const searchFieldVtds = debouncedVehicleSearch ? 'license_plate' : '';
  const rawMasterVehicles = useVtdsList(0, 2000, searchFieldVtds, debouncedVehicleSearch, filterCompanyIds);
  const { data: rawDriverAssigns } = useDriverAssignList(0, 5000, '', '', filterCompanyIds);

  const driverAssignList = useMemo(() => {
    if (Array.isArray(rawDriverAssigns)) return rawDriverAssigns;
    if (Array.isArray(rawDriverAssigns?.data)) return rawDriverAssigns.data;
    return [];
  }, [rawDriverAssigns]);

  const masterVehicles = useMemo(() => {
    if (Array.isArray(rawMasterVehicles)) return rawMasterVehicles;
    if (Array.isArray(rawMasterVehicles?.data)) return rawMasterVehicles.data;
    if (rawMasterVehicles && typeof rawMasterVehicles === 'object') return Object.values(rawMasterVehicles);
    return [];
  }, [rawMasterVehicles]);

  const filteredMasterVehicles = useMemo(() => {
    if (!selectedCompanies || selectedCompanies.length === 0) return masterVehicles;
    return masterVehicles.filter((v) => {
      const vCompId = String(v.company_id ?? v.company ?? v.companyId ?? '');
      return selectedCompanies.includes(vCompId);
    });
  }, [masterVehicles, selectedCompanies]);

  const vehicleOptions = useMemo(() => {
    const map = new Map();
    filteredMasterVehicles.forEach((v, idx) => {
      const rawId = v.integration_imei;
      // ?? v.id ?? v._id ?? v.vehicleId ?? v.value ?? `fallback-${idx}`;
      const val = String(rawId);
      if (!map.has(val) && val !== '') {
        map.set(val, {
          value: val,
          label: v.license_plate ?? v.name ?? v.vehicle ?? v.vehicle_name ?? String(val),
          cp: v.join.company_name
        });
      }
    });
    return Array.from(map.values());
  }, [filteredMasterVehicles]);

  const filteredVehicleOptions = useMemo(() => {
    if (!vehicleSearchText) return vehicleOptions;
    return vehicleOptions.filter((v) => v.label.toLowerCase().includes(vehicleSearchText.toLowerCase()));
  }, [vehicleOptions, vehicleSearchText]);

  const visibleIds = useMemo(() => filteredVehicleOptions.map((v) => v.value), [filteredVehicleOptions]);
  const isAllVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => assignedVehicles.includes(id));
  const isSomeVisibleSelected = visibleIds.some((id) => assignedVehicles.includes(id));
  const isIndeterminateVehicles = isSomeVisibleSelected && !isAllVisibleSelected;

  useEffect(() => {
    setAssignedVehicles((prev) => {
      if (!prev || prev.length === 0) return prev;
      const validValues = new Set(vehicleOptions.map((v) => v.value));
      const next = prev.filter((v) => validValues.has(v));
      if (next.length === prev.length) return prev;
      return next;
    });
  }, [vehicleOptions]);

  const renderVehicleValue = (selected) => {
    if (!selected || selected.length === 0) return 'Select Vehicle';
    return `${selected.length} Vehicle Selected`;
  };

  const handleVehicleChange = (event) => {
    const { value } = event.target;
    const selectedValues = typeof value === 'string' ? value.split(',') : value;
    if (selectedValues.includes('select-all-option')) {
      if (isAllVisibleSelected) {
        setAssignedVehicles((prev) => prev.filter((id) => !visibleIds.includes(id)));
      } else {
        setAssignedVehicles((prev) => Array.from(new Set([...prev, ...visibleIds])));
      }
    } else {
      setAssignedVehicles(selectedValues);
    }
  };

  const [alarmTypes, setAlarmTypes] = useState([]);
  const [alarmLoading, setAlarmLoading] = useState(false);

  const disabledAlarmIds = useMemo(
    () => new Set(['106', '107', '100', '109', '115', '119', '126', '127', '129', '130', '131', '132', '134']),
    []
  );

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setAlarmLoading(true);
        const langParam = language === 'en' ? 'eng' : language === 'zh' ? 'zh' : 'id';
        const resp = await apiHowen.get(`/alarm-types?translate=${langParam}`);
        const data = resp?.data ?? resp;
        const rows = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : (data?.result ?? []);
        if (!mounted) return;
        setAlarmTypes(rows);
        setSelectedAlrms((prev) => (prev || []).filter((id) => !disabledAlarmIds.has(String(id))));
      } catch {
        if (!mounted) return;
      } finally {
        if (mounted) setAlarmLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [disabledAlarmIds, language]);

  const grouped = useMemo(() => {
    const g = {};
    (alarmTypes || []).forEach((a) => {
      const key = a.type ?? 'UNKNOWN';
      if (!g[key]) g[key] = [];
      g[key].push(a);
    });
    return g;
  }, [alarmTypes]);

  const allIds = useMemo(() => alarmTypes.map((a) => String(a.id)), [alarmTypes]);
  const selectableIds = useMemo(() => allIds.filter((id) => !disabledAlarmIds.has(id)), [allIds, disabledAlarmIds]);

  const toggleOne = (id) => {
    if (disabledAlarmIds.has(id)) return;
    setSelectedAlrms((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };
  const selectAll = () => setSelectedAlrms(selectableIds);
  const deselectAll = () => setSelectedAlrms([]);
  const selectGroup = (type) => {
    const ids = (grouped[type] || []).map((i) => String(i.id)).filter((id) => !disabledAlarmIds.has(id));
    setSelectedAlrms((prev) => Array.from(new Set([...prev, ...ids])));
  };
  const deselectGroup = (type) => {
    const ids = new Set((grouped[type] || []).map((i) => String(i.id)));
    setSelectedAlrms((prev) => prev.filter((id) => !ids.has(id)));
  };

  const anchorRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const handleOpenMenu = (e) => {
    anchorRef.current = e.currentTarget;
    setMenuOpen(true);
  };
  const handleCloseMenu = () => setMenuOpen(false);

  useEffect(() => {
    if (!rangeOption) return;
    if (rangeOption === 'custom' && fromDate && toDate) return;

    const now = dayjs();
    if (rangeOption === 'custom') {
      if (!fromDate) {
        setFromDate('');
        setToDate('');
      }
      return;
    }

    let from;
    let to = now;

    if (rangeOption === 'today') {
      from = now.startOf('day');
    } else if (rangeOption === 'week') {
      from = now.subtract(7, 'day').startOf('day');
      to = now.endOf('day');
    } else if (rangeOption === 'month') {
      from = now.subtract(1, 'month').startOf('day');
      to = now.endOf('day');
    } else {
      return;
    }

    if (from && to) {
      setFromDate(from.format('YYYY-MM-DD HH:mm:ss'));
      setToDate(to.format('YYYY-MM-DD HH:mm:ss'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeOption]);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchedInfo, setSearchedInfo] = useState('');
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [howenToken, setHowenToken] = useState('');

  // --- 1. STATE UNTUK DIALOG DETAIL ---
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailData, setDetailData] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const resp = await apiHowen.get('/get-token');
        const data = resp?.data ?? resp;
        const token = data?.data?.token ?? data?.token ?? data ?? '';
        if (!mounted) return;
        const tokStr = typeof token === 'string' ? token : JSON.stringify(token);
        setHowenToken(tokStr);
        try {
          localStorage.setItem('howen_token', tokStr);
        } catch (e) {
          console.log(e);
        }
      } catch (e) {
        console.log(e);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const buildPayload = useCallback(
    (vid, apiBeginTime, apiEndTime) => {
      const token = howenToken || '';

      return {
        alarmType: (selectedAlrms || []).join(','),
        beginTime: apiBeginTime,
        driverCardId: '',
        endTime: apiEndTime,
        keyword: '',
        lang: 'en_US',
        pageCount: 2000,
        pageNum: 1,
        scheme: 'http',
        token,
        deviceID: String(vid)
      };
    },
    [selectedAlrms, howenToken]
  );

  const fetchAlarms = async () => {
    const hasVehicles = Array.isArray(assignedVehicles) && assignedVehicles.length > 0;
    const hasAlarms = Array.isArray(selectedAlrms) && selectedAlrms.length > 0;
    const hasDates = !!fromDate && !!toDate;

    if (!hasVehicles || !hasAlarms || !hasDates) {
      setRows([]);
      return;
    }

    setLoading(true);
    try {
      let dFrom = dayjs(fromDate);
      let dTo = dayjs(toDate);
      setSearchedInfo(`${dFrom.format('DD MMM YYYY HH:mm:ss')} - ${dTo.format('DD MMM YYYY HH:mm:ss')}`);

      let apiChunks = [];
      const localStart = dayjs(fromDate);
      const localEnd = dayjs(toDate);
      const pType = rangeOption;
      const isFullDayPeriod = ['week', 'last_week', 'month', 'last_month'].includes(pType);

      if (isFullDayPeriod) {
        let curr = localStart.startOf('day');
        const end = localEnd.startOf('day');
        while (curr.isBefore(end) || curr.isSame(end, 'day')) {
          let dStart = curr.startOf('day');
          let dEnd = curr.endOf('day');
          apiChunks.push({
            start: dStart.format('YYYY-MM-DD HH:mm:ss'),
            end: dEnd.format('YYYY-MM-DD HH:mm:ss')
          });
          curr = curr.add(1, 'day');
        }
      } else {
        // Chunk by local days for this specific API which expects local time strings
        let curr = localStart.startOf('day');
        const end = localEnd.startOf('day');

        while (curr.isBefore(end) || curr.isSame(end, 'day')) {
          let chunkStart = curr.startOf('day');
          let chunkEnd = curr.endOf('day');

          if (curr.isSame(localStart, 'day')) chunkStart = localStart;
          if (curr.isSame(localEnd, 'day')) chunkEnd = localEnd;

          apiChunks.push({
            start: chunkStart.format('YYYY-MM-DD HH:mm:ss'),
            end: chunkEnd.format('YYYY-MM-DD HH:mm:ss')
          });

          curr = curr.add(1, 'day');
        }
      }

      const promises = [];
      assignedVehicles.forEach((vid) => {
        apiChunks.forEach((chunk) => {
          const apiBeginTime = chunk.start;
          const apiEndTime = chunk.end;

          const payload = buildPayload(vid, apiBeginTime, apiEndTime);
          promises.push(apiVss.post('/alarm/apiFindAllByTime.action', payload));
        });
      });

      const responses = await Promise.all(promises);

      let combinedList = [];
      responses.forEach((resp) => {
        const body = resp?.data ?? resp;
        const wrapper = body?.data ?? body;
        const list = Array.isArray(wrapper?.dataList)
          ? wrapper.dataList
          : Array.isArray(wrapper?.rows)
            ? wrapper.rows
            : Array.isArray(wrapper?.result)
              ? wrapper.result
              : [];
        combinedList = [...combinedList, ...list];
      });

      const mapped = combinedList.map((it, idx) => {
        const id = String(it?.guid ?? it?.id ?? `alarm-${idx}`);
        const vehicle = it?.plateNo ?? it?.deviceName ?? it?.fleetName ?? (it?.vehicleId ? String(it.vehicleId) : '-');
        const deviceName = it?.deviceName ?? '-';
        const drivername = findDriverNameForAlarm(it, driverAssignList);
        const alarmTypeValue = it?.alarmTypeValue ?? it?.alarmTypeName ?? it?.alarmtype ?? '-';
        const createtime = it?.createtime ?? it?.createTime ?? it?.reportTime ?? '-';
        const reportTime = it?.reportTime ?? it?.endTime ?? '-';
        const speed = it?.speed ?? it?.spds ?? it?.vehicleSpeed ?? '';
        return { id, vehicle, deviceName, drivername, alarmTypeValue, createtime, reportTime, speed, _raw: { ...it, drivername } };
      });

      mapped.sort((a, b) => new Date(b.createtime) - new Date(a.createtime));
      setRows(mapped);
    } catch (err) {
      console.log(err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  // --- 2. FUNGSI UNTUK MEMBUKA DIALOG ---
  const handleShowDetail = useCallback(
    (data) => {
      const vid = data?.vehicleId ?? data?._raw?.vehicleId;
      let vehicleFromMaster = null;
      if (vid != null) {
        const vidStr = String(vid);
        vehicleFromMaster =
          masterVehicles.find((mv) => {
            const mvId = mv?.integration_imei ?? mv?.id ?? mv?._id ?? mv?.vehicleId ?? mv?.value;
            return String(mvId) === vidStr;
          }) || null;
      }

      const merged = {
        ...data,
        vehicleData: vehicleFromMaster || null
      };

      setDetailData(merged);
      setDetailOpen(true);
    },
    [masterVehicles]
  );

  // --- 3. COLUMNS & RENDER ---
  const columns = useMemo(
    () => [
      { field: 'vehicle', headerName: t('reportFatigue.vehicleCol', 'Vehicle'), flex: 0.7 },
      { field: 'drivername', headerName: t('reportFatigue.driverCol', 'Driver'), flex: 0.7 },
      { field: 'alarmTypeValue', headerName: t('reportFatigue.alarmCol', 'Alarm'), flex: 0.7 },
      {
        field: 'createtime',
        headerName: t('reportFatigue.createTimeCol', 'Create Time'),
        width: 180,
        valueGetter: (value, params) => {
          if (!params.createtime || params.createtime === '-') return '-';
          return dayjs(params.createtime).format('DD/MM/YY HH:mm:ss');
        }
      },
      {
        field: 'reportTime',
        headerName: t('reportFatigue.reportTimeCol', 'Report Time'),
        width: 180,
        valueGetter: (value, params) => {
          if (!params.reportTime || params.reportTime === '-') return '-';
          return dayjs(params.reportTime).format('DD/MM/YY HH:mm:ss');
        }
      },
      { field: 'speed', headerName: t('reportFatigue.speedCol', 'Speed'), flex: 0.6 },
      {
        field: 'action',
        headerName: t('reportFatigue.actionCol', 'Action'),
        flex: 0.4,
        sortable: false,
        renderCell: (params) => {
          const row = params.row;
          return (
            <IconButton
              size="small"
              onClick={() => {
                handleShowDetail(row._raw);
              }}
            >
              <VisibilityIcon />
            </IconButton>
          );
        }
      }
    ],
    [handleShowDetail, t]
  );

  const rowsWithDriver = useMemo(
    () =>
      rows.map((row) => {
        const rawItem = row._raw || row;
        const drivername = findDriverNameForAlarm(rawItem, driverAssignList);
        return {
          ...row,
          drivername,
          _raw: row._raw ? { ...row._raw, drivername } : row._raw
        };
      }),
    [rows, driverAssignList]
  );

  const filteredRows = useMemo(() => {
    if (!searchQuery) return rowsWithDriver;
    const query = searchQuery.toLowerCase().trim();
    return rowsWithDriver.filter((row) => {
      const valueToSearch = row[searchField] ? String(row[searchField]).toLowerCase() : '';
      return valueToSearch.includes(query);
    });
  }, [rowsWithDriver, searchQuery, searchField]);

  const globalMenuProps = {
    PaperProps: {
      sx: {
        width: 220,
        bgcolor: '#EDF3FC',
        '& .MuiMenuItem-root': { minHeight: 40 }
      }
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <Stack spacing={1}>
          <FormControl
            sx={{
              minWidth: 200,
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: '#485D9A' },
                '&:hover fieldset': { borderColor: '#485D9A' },
                '&.Mui-focused fieldset': { borderColor: '#485D9A' }
              },
              '& .MuiInputLabel-root.Mui-focused': { color: '#485D9A' }
            }}
          >
            <InputLabel id="company-select-label">{t('general.company', 'Company')}</InputLabel>
            <Select
              labelId="company-select-label"
              multiple
              value={selectedCompanies}
              onChange={handleCompanyChange}
              label={t('general.company', 'Company')}
              MenuProps={globalMenuProps}
              renderValue={renderCompanyValue}
              onClose={() => setCompanySearchText('')}
            >
              <ListSubheader sx={{ bgcolor: 'inherit', pt: 1, pb: 1 }}>
                <TextField
                  size="small"
                  autoFocus
                  placeholder="Search company..."
                  fullWidth
                  value={companySearchText}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    )
                  }}
                  onChange={(e) => setCompanySearchText(e.target.value)}
                  onKeyDown={(e) => e.stopPropagation()}
                />
              </ListSubheader>
              <MenuItem value="select-all-option">
                <Checkbox checked={isAllCompaniesSelected} indeterminate={isIndeterminateCompanies} />
                <ListItemText primary={t('buttonTitle.selectAll', 'Select All')} />
              </MenuItem>
              <Divider />
              {filteredCompanyOptions.length > 0 ? (
                filteredCompanyOptions.map((opt, idx) => (
                  <MenuItem key={`company-${opt.value}-${idx}`} value={opt.value}>
                    <Checkbox checked={selectedCompanies.indexOf(opt.value) > -1} />
                    <ListItemText primary={opt.label} />
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled>
                  <ListItemText primary="No company found" />
                </MenuItem>
              )}
            </Select>
          </FormControl>
        </Stack>

        <Stack spacing={1}>
          <FormControl
            sx={{
              minWidth: 200,
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: '#485D9A' },
                '&:hover fieldset': { borderColor: '#485D9A' },
                '&.Mui-focused fieldset': { borderColor: '#485D9A' }
              },
              '& .MuiInputLabel-root.Mui-focused': { color: '#485D9A' }
            }}
          >
            <InputLabel id="vehicle-select-label">{t('report.vehicle', 'Vehicle')}</InputLabel>
            <Select
              labelId="vehicle-select-label"
              multiple
              value={assignedVehicles}
              onChange={handleVehicleChange}
              label={t('report.vehicle', 'Vehicle')}
              MenuProps={globalMenuProps}
              renderValue={renderVehicleValue}
              onClose={() => setVehicleSearchText('')}
            >
              <ListSubheader sx={{ bgcolor: 'inherit', pt: 1, pb: 1 }}>
                <TextField
                  size="small"
                  autoFocus
                  placeholder="Search vehicle..."
                  fullWidth
                  value={vehicleSearchText}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    )
                  }}
                  onChange={(e) => setVehicleSearchText(e.target.value)}
                  onKeyDown={(e) => e.stopPropagation()}
                />
              </ListSubheader>
              <MenuItem value="select-all-option">
                <Checkbox checked={isAllVisibleSelected} indeterminate={isIndeterminateVehicles} />
                <ListItemText primary={t('buttonTitle.selectAll', 'Select All')} />
              </MenuItem>
              <Divider />
              {filteredVehicleOptions.length > 0 ? (
                filteredVehicleOptions.map((opt, idx) => (
                  <MenuItem key={`vehicle-${opt.value}-${idx}`} value={opt.value}>
                    <Checkbox checked={assignedVehicles.indexOf(opt.value) > -1} />
                    <ListItemText primary={opt.label} secondary={opt.cp} />
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled>
                  <ListItemText primary="No vehicle found" />
                </MenuItem>
              )}
            </Select>
          </FormControl>
        </Stack>

        <Stack spacing={1}>
          <TextField
            label={t('report.alrm', 'Alrm')}
            value={selectedAlrms.length ? `${selectedAlrms.length} Alrm Selected` : ''}
            placeholder={t('report.selectAlrm', 'Click to select alrms')}
            InputProps={{ readOnly: true }}
            onClick={handleOpenMenu}
            sx={{
              minWidth: 180,
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: '#485D9A' },
                '&:hover fieldset': { borderColor: '#485D9A' },
                '&.Mui-focused fieldset': { borderColor: '#485D9A' }
              },
              '& .MuiInputLabel-root.Mui-focused': { color: '#485D9A' }
            }}
          />
          <Menu
            anchorEl={anchorRef.current}
            open={menuOpen}
            onClose={handleCloseMenu}
            PaperProps={{ sx: { width: 1000, p: 1, bgcolor: '#EDF3FC' } }}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          >
            <Box sx={{ px: 1, py: 0.5 }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Checkbox
                  checked={selectedAlrms.length === selectableIds.length && selectableIds.length > 0}
                  indeterminate={selectedAlrms.length > 0 && selectedAlrms.length < selectableIds.length}
                  onChange={(e) => (e.target.checked ? selectAll() : deselectAll())}
                />
                <Typography variant="body2">{t('buttonTitle.selectAll', 'Select All')}</Typography>
              </Box>
              <Divider sx={{ mb: 1 }} />
              {Object.keys(grouped).map((type) => {
                const items = grouped[type];
                const selectableInGroup = items.map((i) => String(i.id)).filter((id) => !disabledAlarmIds.has(id));
                const isAllTypeSelected = selectableInGroup.length > 0 && selectableInGroup.every((id) => selectedAlrms.includes(id));
                const isSomeTypeSelected = selectableInGroup.some((id) => selectedAlrms.includes(id));
                const columnsCount = type === 'ADAS' ? 2 : type === 'DMS' ? 3 : 3;
                return (
                  <Box key={type} sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 1, mb: 1 }}>
                      <Checkbox
                        checked={isAllTypeSelected}
                        indeterminate={!isAllTypeSelected && isSomeTypeSelected}
                        onChange={(e) => (e.target.checked ? selectGroup(type) : deselectGroup(type))}
                      />
                      <Typography variant="subtitle2">Select {type}</Typography>
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${columnsCount}, 1fr)`, gap: 1 }}>
                      {items.map((it, idx) => {
                        const id = String(it.id);
                        const checked = selectedAlrms.includes(id);
                        const disabled = disabledAlarmIds.has(id);
                        return (
                          <ButtonBase
                            key={`alarm-${id}-${idx}`}
                            onClick={() => toggleOne(id)}
                            sx={{
                              borderRadius: 1,
                              width: '100%',
                              textAlign: 'left',
                              justifyContent: 'flex-start',
                              opacity: disabled ? 0.6 : 1,
                              cursor: disabled ? 'not-allowed' : 'pointer'
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                              <Checkbox checked={checked} disabled={disabled} />
                              <Box sx={{ overflow: 'hidden', minWidth: 0 }}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    display: 'block',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    maxWidth: '100%',
                                    textAlign: 'left'
                                  }}
                                >
                                  {it.name}
                                </Typography>
                              </Box>
                            </Box>
                          </ButtonBase>
                        );
                      })}
                    </Box>
                  </Box>
                );
              })}
              {alarmLoading && <Typography variant="body2">Loading...</Typography>}
            </Box>
          </Menu>
        </Stack>

        <Stack spacing={1}>
          <FormControl
            sx={{
              minWidth: 180,
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: '#485D9A' },
                '&:hover fieldset': { borderColor: '#485D9A' },
                '&.Mui-focused fieldset': { borderColor: '#485D9A' }
              },
              '& .MuiInputLabel-root.Mui-focused': { color: '#485D9A' }
            }}
          >
            <InputLabel id="range-select-label">{t('reportFatigue.periodRange', 'Periode Range')}</InputLabel>
            <Select
              labelId="range-select-label"
              value={rangeOption}
              label={t('reportFatigue.periodRange', 'Periode Range')}
              onChange={(e) => setRangeOption(e.target.value)}
            >
              <MenuItem value="today">Today</MenuItem>
              <MenuItem value="week">{t('reportFatigue.lastWeek', 'Last Week')}</MenuItem>
              <MenuItem value="month">{t('reportFatigue.lastMonth', 'Last Month')}</MenuItem>
              <MenuItem value="custom">{t('reportFatigue.custom', 'Custom')}</MenuItem>
            </Select>
          </FormControl>
        </Stack>

        {rangeOption === 'custom' && (
          <Stack spacing={1}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <DateTimePicker
                label={t('reportFatigue.from', 'From')}
                format="DD/MM/YYYY HH:mm:ss"
                ampm={false}
                timeSteps={{ minutes: 1, seconds: 1 }}
                value={fromDate ? dayjs(fromDate) : null}
                maxDate={toDate ? dayjs(toDate) : undefined}
                onChange={(v) => setFromDate(v ? v.format('YYYY-MM-DD HH:mm:ss') : '')}
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: {
                      minWidth: 160,
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: '#485D9A' },
                        '&:hover fieldset': { borderColor: '#485D9A' },
                        '&.Mui-focused fieldset': { borderColor: '#485D9A' }
                      },
                      '& .MuiInputLabel-root.Mui-focused': { color: '#485D9A' }
                    }
                  }
                }}
              />
              <DateTimePicker
                label={t('reportFatigue.to', 'To')}
                format="DD/MM/YYYY HH:mm:ss"
                ampm={false}
                timeSteps={{ minutes: 1, seconds: 1 }}
                value={toDate ? dayjs(toDate) : null}
                minDate={fromDate ? dayjs(fromDate) : undefined}
                onChange={(v) => setToDate(v ? v.format('YYYY-MM-DD HH:mm:ss') : '')}
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: {
                      minWidth: 160,
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: '#485D9A' },
                        '&:hover fieldset': { borderColor: '#485D9A' },
                        '&.Mui-focused fieldset': { borderColor: '#485D9A' }
                      },
                      '& .MuiInputLabel-root.Mui-focused': { color: '#485D9A' }
                    }
                  }
                }}
              />
            </Box>
          </Stack>
        )}

        <Stack spacing={1}>
          <Button
            variant="contained"
            onClick={fetchAlarms}
            disabled={loading || assignedVehicles.length === 0 || selectedAlrms.length === 0 || !fromDate || !toDate}
            sx={{ backgroundColor: '#485D9A', '&:hover': { backgroundColor: '#3b4d82' } }}
          >
            {t('general.search', 'Search')}
          </Button>
        </Stack>
      </Box>

      {searchedInfo && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body1" sx={{ color: '#485D9A', fontWeight: 500 }}>
            {t('reportFatigue.showingDataFrom', 'Showing data from')}: {searchedInfo}
          </Typography>
        </Box>
      )}

      <Box sx={{ mt: 2 }}>
        <DataTable
          rows={filteredRows}
          columns={columns}
          loading={loading}
          pageSizeOptions={[25, 50, 100]}
          paginationMode="client"
          paginationModel={paginationModel}
          onPaginationModelChange={(model) => setPaginationModel(model)}
          searchQuery={searchQuery}
          onSearchChange={(e) => setSearchQuery(e.target.value)}
          searchFieldOptions={fatigueSearchOptions}
          selectedSearchField={searchField}
          onSearchFieldChange={(val) => setSearchField(val)}
          searchLabel={`Search by ${fatigueSearchOptions.find((o) => o.value === searchField)?.label}`}
          showDateFilter={false}
          hideExportButton={true}
          hideAddButton={true}
        />
      </Box>

      {/* 4. TAMPILKAN KOMPONEN DETAIL REPORT */}
      <DetailReportFatiguePage open={detailOpen} onClose={() => setDetailOpen(false)} data={detailData} />
    </LocalizationProvider>
  );
}
